/* @flow */
import * as React from 'react';
import { css, names } from 'linaria';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import type { History } from 'react-router';

import withCurrentUser from '../../components/withCurrentUser';
import { Icon, Link, PlainButton, Text, Spacer, Button } from '../core';
import Colors from '../../constants/Colors';
import Touch from '../../../utilities/Touch';
import MediaQueries from '../../constants/MediaQueries';
import ZIndex from '../../constants/ZIndex';

type User = {
  profile: {
    first_name: ?string,
    avatar_url: ?string,
  },
};

const UserAvatar = ({ url }: { url: ?string }) => (
  <div
    className={names(Styles.UserAvatar, !url && Styles.UserAvatarPlaceholder)}
    style={{ backgroundImage: url && `url(${url})` }}>
    <Icon name="user-white" className={url && Styles.UserAvatarHiddenIcon} />
  </div>
);

const MenuItem = ({
  to,
  title,
  onSelect,
}: {
  to?: string,
  title: string,
  onSelect?: () => void,
}) => (
  <li className={names(Styles.MenuItem, Styles.SelectableMenuItem)}>
    {to && (
      <Link className={Styles.Link} to={to}>
        {title}
      </Link>
    )}
    {!to && (
      <PlainButton className={Styles.Link} onClick={onSelect}>
        {title}
      </PlainButton>
    )}
  </li>
);

class UserMenu extends React.Component<
  { currentUser: ?User, history: History, theme: 'light' | 'dark' },
  { open: boolean }
> {
  state = { open: false };
  browserHistoryListener: () => void;
  timeout: ?number;
  pauseToggle = false;

  onClickBody = () => {
    this.close();
  };

  onMouseLeave = () => {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.close, 400);
  };

  onMouseEnter = () => {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(this.open, 200);
  };

  open = () => {
    this.setState({ open: true });
    this.pauseToggle = true;
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => (this.pauseToggle = false), 400);
    window.addEventListener(Touch.supported ? 'touchend' : 'click', this.onClickBody);
  };

  close = () => {
    window.removeEventListener(Touch.supported ? 'touchend' : 'click', this.onClickBody);
    this.setState({ open: false });
  };

  toggle = event => {
    event.stopPropagation();
    clearTimeout(this.timeout);
    if (this.pauseToggle) {
      this.pauseToggle = false;
      return;
    }
    this.setState({ open: !this.state.open });
  };

  componentDidMount() {
    let { history } = this.props;
    this.browserHistoryListener = history.listen(this.close);
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    window.removeEventListener(Touch.supported ? 'touchend' : 'click', this.onClickBody);
    if (this.browserHistoryListener) {
      this.browserHistoryListener();
    }
  }

  render() {
    const { currentUser, theme, onLogOut } = this.props;
    const { open } = this.state;
    if (!currentUser) return null;

    return (
      <div
        className={Styles.Container}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}>
        <PlainButton className={Styles.Button} onClick={this.toggle}>
          <UserAvatar url={currentUser && currentUser.profile.avatar_url} />
          <Icon
            name={theme === 'dark' ? 'chevron-down-white' : 'chevron-down'}
            className={Styles.Chevron}
          />
        </PlainButton>
        {open && (
          <div>
            <div className={Styles.Arrow} />
            <ul className={Styles.Menu}>
              <Text className={Styles.MenuItem}>Hi, {currentUser.profile.first_name}</Text>
              <div className={Styles.BookButtonContainer}>
                <Button to="/book" bordered={false} theme="yellow" className={Styles.BookButton}>
                  Book a Lugg
                </Button>
              </div>
              <MenuItem to="/account/luggs" title="My Luggs" />
              <MenuItem to="/account/profile" title="My Profile" />
              <MenuItem to="/account/payment" title="Payment Info" />
              <div className={Styles.Spacer}>
                <Spacer color={Colors.opacity(Colors.White, 0.1)} />
              </div>
              <MenuItem onSelect={onLogOut} title="Log Out" />
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export default compose(
  withRouter,
  withCurrentUser
)(UserMenu);

const Styles = {
  Container: css`
    position: relative;
    align-self: center;
    margin-top: 15px;
    margin-bottom: 15px;
  `,
  Button: css`
    display: flex;
    align-items: center;
  `,
  UserAvatar: css`
    width: 48px;
    height: 48px;
    border-radius: 24px;
    background-color: ${Colors.White};
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
  `,
  UserAvatarPlaceholder: css`
    background-color: ${Colors.Violet};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  UserAvatarHiddenIcon: css`
    display: none;
  `,
  Chevron: css`
    margin-left: 8px;
  `,
  Menu: css`
    background-color: ${Colors.IndigoDarkest};
    box-shadow: 0 12px 40px 0 rgba(28, 31, 52, 0.15);
    padding: 12px 0;
    display: flex;
    flex-direction: column;
    position: absolute;
    top: calc(100% + 10px);
    left: -100px;
    border-radius: 10px;
    width: 200px;
    z-index: ${ZIndex.Lowest};
  `,
  Arrow: css`
    position: absolute;
    top: calc(100% + 4px);
    left: 25px;
    width: 12px;
    height: 12px;
    transform: rotate(45deg);
    background: ${Colors.IndigoDarkest};
    box-shadow: -3px -3px 5px rgba(82, 95, 127, 0.04);
  `,
  MenuItem: css`
    padding: 6px 32px;
  `,
  SelectableMenuItem: css`
    &:hover {
      background-color: ${Colors.opacity(Colors.White, 0.1)};
    }
  `,
  Spacer: css`
    margin-left: 32px;
    margin-right: 32px;
  `,
  BookButtonContainer: css`
    margin: 8px 0;
    margin-left: 18px;
    margin-right: 18px;
  `,
  BookButton: css`
    padding: 6px 14px 6px 14px;
    width: 100%;
    ${MediaQueries.Medium} {
      padding: 6px 14px 6px 14px;
    }
  `,
  Link: css`
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 16px;
    color: ${Colors.White};
    white-space: nowrap;
    padding: 0;
  `,
};



// WEBPACK FOOTER //
// ./src/ui/components/chrome/UserMenu.js